import { useEffect, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { API, api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeleteAccount } from "@/pages/AccountPage";

const Input = ({ label, testId, ...p }) => (
  <label className="block"><span className="lux-eyebrow mb-2 block">{label}</span><input data-testid={testId} className="lux-input" {...p} /></label>
);

const useForm = (initial) => {
  const [f, setF] = useState(initial);
  return [f, (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value })), setF];
};

const Submit = ({ busy, label, testId }) => <button type="submit" disabled={busy} data-testid={testId} className="btn-gold !py-2.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {label}</button>;

const run = async (setBusy, fn, ok) => {
  setBusy(true);
  try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
};

const Profile = ({ acc, setAcc }) => {
  const { refresh } = useAuth();
  const [f, on] = useForm({ name: acc.name, phone: acc.phone, vehicle_make: acc.vehicle_make, vehicle_model: acc.vehicle_model, vehicle_color: acc.vehicle_color, vehicle_plate: acc.vehicle_plate });
  const [busy, setBusy] = useState(false);
  const save = (e) => { e.preventDefault(); run(setBusy, async () => { setAcc((await api.put("/driver/account", f)).data); refresh?.(); }, "Account saved"); };
  const photo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(setBusy, async () => { const { data } = await api.post("/driver/account/photo", fd); setAcc({ ...acc, photo_url: data.photo_url }); }, "Photo updated");
  };
  return (
    <form onSubmit={save} className="lux-card space-y-4 p-5 sm:p-6" data-testid="account-profile-form">
      <div className="flex items-center gap-4">
        {acc.photo_url ? <img data-testid="account-photo" src={`${API.replace(/\/api$/, "")}${acc.photo_url}`} alt="Profile" className="h-16 w-16 rounded-full object-cover" />
          : <span className="flex h-16 w-16 items-center justify-center rounded-full border border-amber-500/40 font-serif text-2xl text-amber-200">{acc.name[0]}</span>}
        <label className="btn-outline-gold cursor-pointer !py-2 text-sm"><Camera className="h-4 w-4" /> Change photo
          <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="account-photo-input" className="hidden" onChange={photo} /></label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Name" testId="account-name-input" value={f.name} onChange={on("name")} required />
        <Input label="Phone" testId="account-phone-input" value={f.phone} onChange={on("phone")} />
        <Input label="Vehicle make" testId="account-make-input" value={f.vehicle_make} onChange={on("vehicle_make")} />
        <Input label="Vehicle model" testId="account-model-input" value={f.vehicle_model} onChange={on("vehicle_model")} />
        <Input label="Color" testId="account-color-input" value={f.vehicle_color} onChange={on("vehicle_color")} />
        <Input label="Plate" testId="account-plate-input" value={f.vehicle_plate} onChange={on("vehicle_plate")} />
      </div>
      <p className="text-sm text-zinc-400">Vehicle class: <span data-testid="account-vehicle-class" className="capitalize text-amber-200">{acc.vehicle_class}</span> <span className="text-xs text-zinc-500">(set by admin)</span></p>
      <p className="text-sm text-zinc-400">Driver ID <span className="font-mono text-amber-300">{acc.driver_code}</span> · {acc.email}</p>
      <Submit busy={busy} label="Save profile & vehicle" testId="account-save-button" />
    </form>
  );
};

const Bank = ({ acc, setAcc }) => {
  const [f, on, setF] = useForm({ account_holder: acc.bank?.account_holder || acc.name, routing_number: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  const save = (e) => { e.preventDefault(); run(setBusy, async () => { const { data } = await api.put("/driver/account/bank", f); setAcc({ ...acc, bank: data }); setF({ ...f, routing_number: "", account_number: "" }); }, "Bank account saved"); };
  return (
    <form onSubmit={save} className="lux-card space-y-4 p-5 sm:p-6" data-testid="account-bank-form">
      <p className="lux-eyebrow">Bank account for payouts</p>
      {acc.bank && <p data-testid="account-bank-saved" className="text-sm text-emerald-300">Saved: {acc.bank.account_holder} · account ••{acc.bank.last4}</p>}
      <Input label="Account holder" testId="bank-holder-input" value={f.account_holder} onChange={on("account_holder")} required />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Routing number (9 digits)" testId="bank-routing-input" inputMode="numeric" pattern="\d{9}" value={f.routing_number} onChange={on("routing_number")} required />
        <Input label="Account number" testId="bank-account-input" inputMode="numeric" pattern="\d{4,17}" value={f.account_number} onChange={on("account_number")} required />
      </div>
      <p className="text-xs text-zinc-500">Stored encrypted. Only the last 4 digits are ever shown.</p>
      <Submit busy={busy} label={acc.bank ? "Replace bank account" : "Save bank account"} testId="bank-save-button" />
    </form>
  );
};

const Password = () => {
  const [f, on, setF] = useForm({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = (e) => { e.preventDefault(); run(setBusy, async () => { await api.post("/auth/change-password", f); setF({ current_password: "", new_password: "" }); }, "Password changed"); };
  return (
    <form onSubmit={save} className="lux-card space-y-4 p-5 sm:p-6" data-testid="account-password-form">
      <p className="lux-eyebrow">Change password</p>
      <Input label="Current password" testId="current-password-input" type="password" autoComplete="current-password" value={f.current_password} onChange={on("current_password")} required />
      <Input label="New password (8+ characters)" testId="new-password-input" type="password" autoComplete="new-password" minLength={8} value={f.new_password} onChange={on("new_password")} required />
      <Submit busy={busy} label="Change password" testId="password-save-button" />
    </form>
  );
};

export default function DriverAccount() {
  const [acc, setAcc] = useState(null);
  useEffect(() => { api.get("/driver/account").then(({ data }) => setAcc(data)); }, []);
  return (
    <PortalShell>
      <p className="lux-eyebrow">Profile, vehicle & payouts</p>
      <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Account</h1>
      {acc && (
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <Profile acc={acc} setAcc={setAcc} />
          <div className="min-w-0 space-y-6"><Bank acc={acc} setAcc={setAcc} /><Password /><DeleteAccount /></div>
        </div>
      )}
    </PortalShell>
  );
}
